import { useState, useEffect } from 'react';
import { Search, Edit2, Save, X, ChevronLeft, ChevronRight, Users } from 'lucide-react';

interface User {
  id: string;
  email: string;
  name: string | null;
  name_en: string | null;
  phone: string | null;
  gender: string | null;
  birth_date: string | null;
  role: 'user' | 'provider' | 'admin';
  provider: string;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
  avatar_url: string | null;
  // 교육업체 전용 필드
  company_name: string | null;
  ceo_name: string | null;
  ceo_name_en: string | null;
}

interface EditForm {
  name: string;
  name_en: string;
  phone: string;
  gender: string;
  birth_date: string;
  role: 'user' | 'provider' | 'admin';
  company_name: string;
  ceo_name: string;
  ceoFirstName: string;
  ceoLastName: string;
}

export default function AdminUsersTab() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({
    name: '',
    name_en: '',
    phone: '',
    gender: '',
    birth_date: '',
    role: 'user',
    company_name: '',
    ceo_name: '',
    ceoFirstName: '',
    ceoLastName: '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 페이지네이션
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  // 검색 활성화 여부 (전체보기 버튼 표시용)
  const [activeSearch, setActiveSearch] = useState('');

  // 영문 이름 분리 - 기존 데이터는 순서를 알 수 없으므로 전체를 firstName에 표시
  const splitEnglishName = (fullName: string | null): { firstName: string; lastName: string } => {
    if (!fullName) return { firstName: '', lastName: '' };
    return { firstName: fullName.trim(), lastName: '' };
  };

  // 이름 합치기 및 정리
  const cleanAndConcatenateName = (firstName: string, lastName: string): string => {
    const cleanFirst = firstName.trim().replace(/\s+/g, ' ');
    const cleanLast = lastName.trim().replace(/\s+/g, ' ');
    if (!cleanLast) return cleanFirst;
    return `${cleanFirst} ${cleanLast}`.trim();
  };

  // 회원 목록 조회
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ...(searchTerm && { search: searchTerm }),
      });

      const response = await fetch(`/api/v1/admin/users?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('회원 목록을 불러오는데 실패했습니다.');
      }

      const data = await response.json();
      setUsers(data.users);
      setTotalPages(data.totalPages);
      setTotalCount(data.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, activeSearch]);

  // 검색
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setActiveSearch(searchTerm);
    fetchUsers();
  };

  // 전체보기 (검색 초기화)
  const handleShowAll = () => {
    setSearchTerm('');
    setActiveSearch('');
    setPage(1);
  };

  // 수정 모달 열기
  const openEditModal = (user: User) => {
    setEditingUser(user);
    const { firstName, lastName } = splitEnglishName(user.ceo_name_en);
    setEditForm({
      name: user.name || '',
      name_en: user.name_en || '',
      phone: user.phone || '',
      gender: user.gender || '',
      birth_date: user.birth_date || '',
      role: user.role,
      company_name: user.company_name || '',
      ceo_name: user.ceo_name || '',
      ceoFirstName: firstName,
      ceoLastName: lastName,
    });
  };

  // 수정 모달 닫기
  const closeEditModal = () => {
    setEditingUser(null);
    setEditForm({
      name: '',
      name_en: '',
      phone: '',
      gender: '',
      birth_date: '',
      role: 'user',
      company_name: '',
      ceo_name: '',
      ceoFirstName: '',
      ceoLastName: '',
    });
  };

  // 수정 저장
  const saveEdit = async () => {
    if (!editingUser) return;

    setSaving(true);
    setMessage(null);
    try {
      // CEO 영문명 합치기
      const ceo_name_en = cleanAndConcatenateName(editForm.ceoFirstName, editForm.ceoLastName);

      const response = await fetch(`/api/v1/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: editForm.name,
          name_en: editForm.name_en,
          phone: editForm.phone,
          gender: editForm.gender,
          birth_date: editForm.birth_date,
          role: editForm.role,
          company_name: editForm.company_name,
          ceo_name: editForm.ceo_name,
          ceo_name_en,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || '수정에 실패했습니다.');
      }

      setMessage({ type: 'success', text: '회원 정보가 수정되었습니다.' });
      closeEditModal();
      fetchUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : '오류가 발생했습니다.' });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatBirthDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const getGenderText = (gender: string | null) => {
    if (!gender) return '-';
    return gender === 'male' ? '남성' : gender === 'female' ? '여성' : gender;
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">관리자</span>;
      case 'provider':
        return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">교육업체</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">일반</span>;
    }
  };

  // provider 역할인지 확인
  const isProvider = (role: string) => role === 'provider';

  return (
    <div>
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Users className="w-6 h-6" />
          회원 정보 관리
        </h1>
        <p className="text-gray-500 mt-1">전체 회원 {totalCount}명</p>
      </div>

      {/* 메시지 */}
      {message && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* 검색 */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="이름 또는 이메일로 검색..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            검색
          </button>
          {activeSearch && (
            <button
              type="button"
              onClick={handleShowAll}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              전체보기
            </button>
          )}
        </div>
        {activeSearch && (
          <p className="mt-2 text-sm text-gray-500">
            "<span className="font-medium text-gray-700">{activeSearch}</span>" 검색 결과: {totalCount}명
          </p>
        )}
      </form>

      {/* 테이블 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            로딩 중...
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">{error}</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">회원이 없습니다.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">프로필</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">이름</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">영문명</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">이메일</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">연락처</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">성별</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">생년월일</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">역할</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">업체명</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">가입방식</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">가입일</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">관리</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    {/* 프로필 이미지 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {user.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white text-sm font-medium">
                          {user.name?.charAt(0) || user.email?.charAt(0) || 'U'}
                        </div>
                      )}
                    </td>

                    {/* 이름 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-gray-900 font-medium">{user.name || '-'}</span>
                    </td>

                    {/* 영문명 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-gray-600 text-sm">{user.name_en || '-'}</span>
                    </td>

                    {/* 이메일 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-gray-600 text-sm">{user.email}</span>
                      {user.email_confirmed_at && (
                        <span className="ml-1 text-green-500 text-xs">✓</span>
                      )}
                    </td>

                    {/* 연락처 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-gray-600 text-sm">{user.phone || '-'}</span>
                    </td>

                    {/* 성별 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-gray-600 text-sm">{getGenderText(user.gender)}</span>
                    </td>

                    {/* 생년월일 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-gray-600 text-sm">{formatBirthDate(user.birth_date)}</span>
                    </td>

                    {/* 역할 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getRoleBadge(user.role)}
                    </td>

                    {/* 업체명 (교육업체만 표시) */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-gray-600 text-sm">
                        {isProvider(user.role) ? (user.company_name || '-') : '-'}
                      </span>
                    </td>

                    {/* 가입방식 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-gray-600 text-sm capitalize">{user.provider}</span>
                    </td>

                    {/* 가입일 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-gray-500 text-sm">{formatDate(user.created_at)}</span>
                    </td>

                    {/* 관리 버튼 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-center">
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-1.5 text-primary hover:bg-primary/10 rounded transition-colors"
                          title="수정"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              총 {totalCount}명 중 {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalCount)}명
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-gray-700">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 수정 모달 */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* 오버레이 */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeEditModal}
          />

          {/* 모달 */}
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
            {/* 헤더 */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">회원 정보 수정</h2>
              <button
                onClick={closeEditModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* 폼 */}
            <div className="p-6 space-y-6">
              {/* 기본 정보 */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">기본 정보</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">이름</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">영문명</label>
                    <input
                      type="text"
                      value={editForm.name_en}
                      onChange={e => setEditForm({ ...editForm, name_en: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Hong Gildong"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">이메일</label>
                    <input
                      type="email"
                      value={editingUser.email}
                      disabled
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">연락처</label>
                    <input
                      type="text"
                      value={editForm.phone}
                      onChange={e => setEditForm({ ...editForm, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="010-0000-0000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">성별</label>
                    <select
                      value={editForm.gender}
                      onChange={e => setEditForm({ ...editForm, gender: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    >
                      <option value="">선택안함</option>
                      <option value="male">남성</option>
                      <option value="female">여성</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">생년월일</label>
                    <input
                      type="date"
                      value={editForm.birth_date}
                      onChange={e => setEditForm({ ...editForm, birth_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                </div>
              </div>

              {/* 역할 설정 */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">역할 설정</h3>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">회원 역할</label>
                  <select
                    value={editForm.role}
                    onChange={e => setEditForm({ ...editForm, role: e.target.value as EditForm['role'] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="user">일반 회원</option>
                    <option value="provider">교육업체</option>
                    <option value="admin">관리자</option>
                  </select>
                </div>
              </div>

              {/* 교육업체 정보 */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  교육업체 정보
                  {editForm.role !== 'provider' && (
                    <span className="ml-2 text-xs text-gray-400 font-normal">(일반 회원의 경우 역할을 교육업체로 변경 시 활용됨)</span>
                  )}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-600 mb-1">업체명</label>
                    <input
                      type="text"
                      value={editForm.company_name}
                      onChange={e => setEditForm({ ...editForm, company_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="스탬핏 피트니스"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">대표자명</label>
                    <input
                      type="text"
                      value={editForm.ceo_name}
                      onChange={e => setEditForm({ ...editForm, ceo_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="홍길동"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">대표자 First Name (이름)</label>
                    <input
                      type="text"
                      value={editForm.ceoFirstName}
                      onChange={e => setEditForm({ ...editForm, ceoFirstName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Gildong"
                    />
                    <p className="text-xs text-gray-500 mt-1">기존 이름을 확인하고 First Name과 Last Name으로 나눠서 입력해주세요.</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">대표자 Last Name (성)</label>
                    <input
                      type="text"
                      value={editForm.ceoLastName}
                      onChange={e => setEditForm({ ...editForm, ceoLastName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Hong"
                    />
                    <p className="text-xs text-gray-500 mt-1">인증서에 "Gildong Hong" 형식으로 표시됩니다.</p>
                  </div>
                </div>
              </div>

              {/* 기타 정보 (읽기 전용) */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">기타 정보</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">가입방식:</span>
                    <span className="ml-2 text-gray-700 capitalize">{editingUser.provider}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">가입일:</span>
                    <span className="ml-2 text-gray-700">{formatDate(editingUser.created_at)}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">마지막 로그인:</span>
                    <span className="ml-2 text-gray-700">{formatDate(editingUser.last_sign_in_at)}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">이메일 인증:</span>
                    <span className={`ml-2 ${editingUser.email_confirmed_at ? 'text-green-600' : 'text-red-500'}`}>
                      {editingUser.email_confirmed_at ? '완료' : '미완료'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 푸터 */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={closeEditModal}
                disabled={saving}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                취소
              </button>
              <button
                onClick={saveEdit}
                disabled={saving}
                className="px-4 py-2 bg-primary text-white hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    저장 중...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    저장
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
